import { Spin, Empty, Typography, Table, Tabs, Button, Modal, Form, Input, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDate } from '../../helpers/date';

interface SiteTableDataType {
  key: number;
  intPkCompanySiteID: number;
  intPkCompanyID: number;
  siteName: string;
  siteAddress: string;
  enable: number;
  dateCreated: string;
}

interface StaffTableDataType {
  key: number;
  intPkStaffID: number;
  intPkCompanyID: number;
  staffName: string;
  staffEmail: string;
  staffRole: string;
  isActive: number;
  staffCreatedAt: string;
}

const CompanyDetailsComponent = () => {
  const { intPkCompanyID } = useParams<{ intPkCompanyID: string }>();
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();

  // Sites API
  const [postSitesApiData, sitesApiResponse, sitesApiError, sitesApiLoading, sitesApiMessage, sitesApiErrorCount] =
    useAxiosPost<ResponseProp>('companies/sites');

  // Staff API
  const [postStaffApiData, staffApiResponse, staffApiError, staffApiLoading, staffApiMessage, staffApiErrorCount] =
    useAxiosPost<ResponseProp>('companies/staff');

  // Reset Password API
  const [
    postResetPasswordApiData,
    resetPasswordApiResponse,
    resetPasswordApiError,
    resetPasswordApiLoading,
    resetPasswordApiMessage,
    resetPasswordApiErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/reset-staff-password');

  const [allSites, setAllSites] = useState<SiteTableDataType[]>([]);
  const [allStaff, setAllStaff] = useState<StaffTableDataType[]>([]);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffTableDataType | null>(null);
  const [resetPasswordForm] = Form.useForm();
  const postSitesApiDataRef = useRef(postSitesApiData);
  const postStaffApiDataRef = useRef(postStaffApiData);
  const lastProcessedErrorCount = useRef({ sites: 0, staff: 0, resetPassword: 0 });

  // Track create-site specific error counts separately
  const lastProcessedSiteErrorCount = useRef({ create: 0 });

  // Create Site
  const [isCreateSiteModalOpen, setIsCreateSiteModalOpen] = useState(false);
  const [siteForm] = Form.useForm();

  const [
    createSiteData,
    createSiteResponse,
    createSiteError,
    createSiteLoading,
    createSiteMessage,
    createSiteErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/create-site');
  const hasProcessedCreateSiteSuccess = useRef(false);
  const lastProcessedCreateSiteResponse = useRef<any>(null);

  // Keep refs updated
  useEffect(() => {
    postSitesApiDataRef.current = postSitesApiData;
    postStaffApiDataRef.current = postStaffApiData;
  }, [postSitesApiData, postStaffApiData]);

  // Fetch data when component mounts or company ID changes
  useEffect(() => {
    if (intPkCompanyID) {
      postSitesApiDataRef.current({ intPkCompanyID: Number(intPkCompanyID) });
      postStaffApiDataRef.current({ intPkCompanyID: Number(intPkCompanyID) });
    }
  }, [intPkCompanyID]);

  // Handle sites response
  useEffect(() => {
    if (sitesApiResponse !== null) {
      const responseData: any = sitesApiResponse.responseData;
      if (Array.isArray(responseData)) {
        const sitesWithKeys = responseData.map((site: any) => ({
          ...site,
          key: site.intPkCompanySiteID,
        }));
        setAllSites(sitesWithKeys);
      } else {
        setAllSites([]);
      }
    }
  }, [sitesApiResponse]);

  // Handle staff response
  useEffect(() => {
    if (staffApiResponse !== null) {
      const responseData: any = staffApiResponse.responseData;
      if (Array.isArray(responseData)) {
        const staffWithKeys = responseData.map((staff: any) => ({
          ...staff,
          key: staff.intPkStaffID,
        }));
        setAllStaff(staffWithKeys);
      } else {
        setAllStaff([]);
      }
    }
  }, [staffApiResponse]);

  // Handle sites error
  useEffect(() => {
    if (
      sitesApiError !== null &&
      sitesApiErrorCount !== 0 &&
      lastProcessedErrorCount.current.sites !== sitesApiErrorCount
    ) {
      lastProcessedErrorCount.current.sites = sitesApiErrorCount;
      openNotification('error', 'Failed to Load Sites', sitesApiError);
    }
  }, [sitesApiError, sitesApiErrorCount, openNotification]);

  // Handle staff error
  useEffect(() => {
    if (
      staffApiError !== null &&
      staffApiErrorCount !== 0 &&
      lastProcessedErrorCount.current.staff !== staffApiErrorCount
    ) {
      lastProcessedErrorCount.current.staff = staffApiErrorCount;
      openNotification('error', 'Failed to Load Staff', staffApiError);
    }
  }, [staffApiError, staffApiErrorCount, openNotification]);

  // Handle reset password success
  useEffect(() => {
    if (resetPasswordApiResponse !== null) {
      openNotification('success', 'Password Reset', 'Staff password reset successfully');
      setIsResetPasswordModalOpen(false);
      resetPasswordForm.resetFields();
      setSelectedStaff(null);
    }
  }, [resetPasswordApiResponse, resetPasswordForm, openNotification]);

  // Handle reset password error
  useEffect(() => {
    if (
      resetPasswordApiError !== null &&
      resetPasswordApiErrorCount !== 0 &&
      lastProcessedErrorCount.current.resetPassword !== resetPasswordApiErrorCount
    ) {
      lastProcessedErrorCount.current.resetPassword = resetPasswordApiErrorCount;
      openNotification('error', 'Failed to Reset Password', resetPasswordApiError);
    }
  }, [resetPasswordApiError, resetPasswordApiErrorCount, openNotification]);

  const handleSiteRowClick = (record: SiteTableDataType) => {
    navigate(`/master-staff-site-details/${record.intPkCompanySiteID}`);
  };

  const showResetPasswordModal = (staff: StaffTableDataType) => {
    setSelectedStaff(staff);
    setIsResetPasswordModalOpen(true);
  };

  const handleResetPasswordCancel = () => {
    if (!resetPasswordApiLoading) {
      setIsResetPasswordModalOpen(false);
      resetPasswordForm.resetFields();
      setSelectedStaff(null);
    }
  };

  const handleResetPassword = (values: { password: string; confirmPassword: string }) => {
    if (!selectedStaff) return;
    postResetPasswordApiData({
      intPkStaffID: selectedStaff.intPkStaffID,
      password: values.password,
      confirmPassword: values.confirmPassword,
    });
  };

  const handleCreateSiteClick = () => {
    siteForm.resetFields();
    setIsCreateSiteModalOpen(true);
  };

  const handleSiteModalCancel = () => {
    if (!createSiteLoading) {
      setIsCreateSiteModalOpen(false);
      siteForm.resetFields();
    }
  };

  const handleCreateSite = (values: { siteName: string; siteAddress: string }) => {
    if (!intPkCompanyID) return;
    const payload = {
      siteName: values.siteName.trim(),
      siteAddress: values.siteAddress.trim(),
    };
    createSiteData({
      intPkCompanyID: Number(intPkCompanyID),
      ...payload,
    } as any);
  };

  // Handle create site success
  useEffect(() => {
    if (createSiteResponse !== null && createSiteResponse !== lastProcessedCreateSiteResponse.current) {
      lastProcessedCreateSiteResponse.current = createSiteResponse;
      hasProcessedCreateSiteSuccess.current = true;
      lastProcessedSiteErrorCount.current.create = createSiteErrorCount;
      const backendMessage = (createSiteResponse as any).message || 'Company site created successfully';
      openNotification('success', 'Site Created', backendMessage);
      setIsCreateSiteModalOpen(false);
      siteForm.resetFields();
      // Refresh sites list
      setTimeout(() => {
        if (intPkCompanyID) {
          postSitesApiDataRef.current({ intPkCompanyID: Number(intPkCompanyID) });
        }
      }, 100);
    }
  }, [createSiteResponse, createSiteErrorCount, openNotification, intPkCompanyID]);

  // Reset flag when createSiteResponse becomes null
  useEffect(() => {
    if (createSiteResponse === null) {
      hasProcessedCreateSiteSuccess.current = false;
      lastProcessedCreateSiteResponse.current = null;
    }
  }, [createSiteResponse]);

  // Handle create site error
  useEffect(() => {
    if (
      createSiteError !== null &&
      createSiteErrorCount !== 0 &&
      lastProcessedSiteErrorCount.current.create !== createSiteErrorCount &&
      !hasProcessedCreateSiteSuccess.current
    ) {
      lastProcessedSiteErrorCount.current.create = createSiteErrorCount;
      openNotification('error', 'Failed to Create Site', createSiteError);
    }
  }, [createSiteError, createSiteErrorCount, openNotification]);

  const siteColumns: ColumnsType<SiteTableDataType> = [
    {
      title: 'Site Name',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Address',
      dataIndex: 'siteAddress',
      key: 'siteAddress',
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'enable',
      key: 'enable',
      render: (enable: number) => {
        const isEnabled = Number(enable) === 1;
        return <Tag color={isEnabled ? 'success' : 'default'}>{isEnabled ? 'Enabled' : 'Disabled'}</Tag>;
      },
    },
    {
      title: 'Date Created',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (date: string) => (date ? formatDate(date) : '-'),
    },
  ];

  const staffColumns: ColumnsType<StaffTableDataType> = [
    {
      title: 'Name',
      dataIndex: 'staffName',
      key: 'staffName',
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Email',
      dataIndex: 'staffEmail',
      key: 'staffEmail',
      render: (text: string) => text || '-',
    },
    {
      title: 'Role',
      dataIndex: 'staffRole',
      key: 'staffRole',
      render: (role: string) => <Typography.Text>{role}</Typography.Text>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: number) => (
        <Typography.Text type={isActive === 1 ? 'success' : 'danger'}>
          {isActive === 1 ? 'Active' : 'Inactive'}
        </Typography.Text>
      ),
    },
    {
      title: 'Date Created',
      dataIndex: 'staffCreatedAt',
      key: 'staffCreatedAt',
      render: (date: string) => (date ? formatDate(date) : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: StaffTableDataType) => (
        <Button type="link" onClick={() => showResetPasswordModal(record)}>
          Reset Password
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'sites',
      label: 'Sites',
      children: (
        <>
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button type="primary" icon={null} onClick={handleCreateSiteClick}>
              Create Site
            </Button>
          </div>
          {sitesApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <Typography style={{ marginTop: '16px', display: 'block' }}>Loading sites...</Typography>
            </div>
          ) : sitesApiError && !sitesApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty
                description={<Typography.Text type="danger">{sitesApiError || 'Failed to load sites'}</Typography.Text>}
              />
            </div>
          ) : allSites.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty description="No sites found for this company." />
            </div>
          ) : (
            <div className="table-container-custom">
              <Table
                className="antd-table-striped sites-table-clickable"
                columns={siteColumns}
                dataSource={allSites}
                pagination={false}
                loading={sitesApiLoading}
                rowClassName={(record) => (Number(record.enable) === 0 ? 'site-disabled' : '')}
                onRow={(record) => ({
                  onClick: () => handleSiteRowClick(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </div>
          )}
        </>
      ),
    },
    {
      key: 'staff',
      label: 'Staff',
      children: (
        <>
          {staffApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <Typography style={{ marginTop: '16px', display: 'block' }}>Loading staff...</Typography>
            </div>
          ) : staffApiError && !staffApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty
                description={<Typography.Text type="danger">{staffApiError || 'Failed to load staff'}</Typography.Text>}
              />
            </div>
          ) : allStaff.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty description="No staff found for this company." />
            </div>
          ) : (
            <div className="table-container-custom">
              <Table
                className="antd-table-striped staff-table-clickable"
                columns={staffColumns}
                dataSource={allStaff}
                pagination={false}
                loading={staffApiLoading}
              />
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Typography.Title level={2} style={{ marginBottom: '8px' }}>Company Details</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
            Company Code: <Typography.Text strong style={{ color: '#1890ff' }}>{intPkCompanyID}</Typography.Text>
          </Typography.Text>
        </div>
        <Link to={`/company-login/${intPkCompanyID}`} style={{ marginBottom: '16px', display: 'inline-block' }} target="_blank">
          <Button type="primary">Login Company</Button>
        </Link>
        <Tabs defaultActiveKey="sites" items={tabItems} />
      </div>

      {/* Create Site Modal */}
      <Modal
        title="Create New Site"
        open={isCreateSiteModalOpen}
        onCancel={handleSiteModalCancel}
        footer={null}
        width={600}
        maskClosable={!createSiteLoading}
        closable={!createSiteLoading}
      >
        <Form form={siteForm} layout="vertical" onFinish={handleCreateSite} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Name</span>}
            name="siteName"
            rules={[
              { required: true, message: 'Please enter site name' },
              { max: 100, message: 'Site name must be less than 100 characters' },
            ]}
          >
            <Input
              placeholder="Enter site name"
              disabled={createSiteLoading}
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Address</span>}
            name="siteAddress"
            rules={[
              { required: true, message: 'Please enter site address' },
              { max: 200, message: 'Site address must be less than 200 characters' },
            ]}
          >
            <Input.TextArea
              placeholder="Enter site address"
              rows={3}
              disabled={createSiteLoading}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleSiteModalCancel} style={{ marginRight: '8px' }} disabled={createSiteLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createSiteLoading}
              disabled={createSiteLoading}
            >
              {createSiteLoading ? 'Creating...' : 'Create Site'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title="Reset Staff Password"
        open={isResetPasswordModalOpen}
        onCancel={handleResetPasswordCancel}
        footer={null}
        width={600}
        maskClosable={!resetPasswordApiLoading}
        closable={!resetPasswordApiLoading}
      >
        <Form form={resetPasswordForm} layout="vertical" onFinish={handleResetPassword} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>New Password</span>}
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter new password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
            ]}
          >
            <Input.Password placeholder="Enter new password" disabled={resetPasswordApiLoading} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Confirm Password</span>}
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: 'Please confirm new password',
              },
              {
                min: 6,
                message: 'Password must be at least 6 characters',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" disabled={resetPasswordApiLoading} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button
              onClick={handleResetPasswordCancel}
              style={{ marginRight: '8px' }}
              disabled={resetPasswordApiLoading}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={resetPasswordApiLoading} disabled={resetPasswordApiLoading}>
              {resetPasswordApiLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CompanyDetailsComponent;
